import { useEffect, useState } from 'react';
import { PageLayout } from '../../PageLayout';
import { PageCard } from '../../Compounds/CardWithTitle';
import { UpstackDataGrid } from '../../Compounds/UpstackDataGrid/UpstackDataGrid';
import { SupplierFormDialog } from '../../Compounds/Advise/SupplierFormDialog';
import { ConfirmDialog } from '../../Compounds/Advise/ConfirmDialog';
import { SupplierGridColumnDefs } from './SupplierGridColumnDefs';
import { AdviseSupplier, SupplierDirectoryRow } from '../../../types/advise';
import { fetchAdviseSupplierDirectory } from '../../../Api/Advise/Suppliers';
import {
  getAdviseSupplierSubject,
  getAdviseSupplierLoadingSubject,
  useAdviseSuppliers
} from '../../../store/AdviseSupplier';
import { hasAdviseFull } from '../../../store/User';
import { GridCellParams, GridSingleSelectColDef } from '@mui/x-data-grid-pro';
import { PermissibleRender } from '../../Compounds/PermissibleRender';
import { Permission } from '../../../store/GeneralStore';
import { AdviseSubNav } from '../../Compounds/Advise/AdviseSubNav';
import { refusalMessage } from '../../../utils/refusalMessage';
import { addError } from '../../../store/Error';
import { DEFAULT_ADVANCE_BUTTON_STYLE } from '../../../utils/styleHelpers';

const SAVE_FAILED = "Couldn't save the supplier. Please try again.";

export const AdviseSuppliersPage = () => {
  const [suppliers, setSuppliers] = useState<AdviseSupplier[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState<AdviseSupplier | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AdviseSupplier | null>(null);
  const [directory, setDirectory] = useState<SupplierDirectoryRow[]>([]);

  const { fetchSuppliers, createSupplier, updateSupplier, deleteSupplier } = useAdviseSuppliers();

  useEffect(() => {
    const supplierSub = getAdviseSupplierSubject().subscribe((data) => setSuppliers(data));
    const loadingSub = getAdviseSupplierLoadingSubject().subscribe((val) => setLoading(val));
    fetchSuppliers();
    return () => {
      supplierSub.unsubscribe();
      loadingSub.unsubscribe();
    };
  }, []);

  // Only offers what to link and which categories to pick, so the form still works without it.
  useEffect(() => {
    if (!showForm) return;
    let current = true;
    const load = async () => {
      const { data, error } = await fetchAdviseSupplierDirectory();
      if (!current) return;
      if (data) setDirectory(data);
      addError('advise-supplier', error?.message);
    };
    load();
    return () => {
      current = false;
    };
  }, [showForm]);

  const handleCreate = () => {
    setEditingSupplier(null);
    setShowForm(true);
  };

  const handleEdit = (supplier: AdviseSupplier) => {
    setEditingSupplier(supplier);
    setShowForm(true);
  };

  const handleSave = async (supplierData: Partial<AdviseSupplier>) => {
    const { data, error } = supplierData.id
      ? await updateSupplier(supplierData.id, supplierData)
      : await createSupplier(supplierData);
    return data ? null : refusalMessage(error, SAVE_FAILED, SAVE_FAILED);
  };

  const handleDelete = async () => {
    if (deleteTarget) {
      await deleteSupplier(deleteTarget.id);
      setDeleteTarget(null);
    }
  };

  const canEdit = hasAdviseFull();

  const actionsColumn: GridSingleSelectColDef = {
    field: 'actions',
    headerName: '',
    sortable: false,
    width: 140,
    filterable: false,
    type: 'singleSelect',
    valueOptions: [],
    hideable: false,
    renderCell: (params: GridCellParams) => {
      if (!canEdit) return null;
      const row = params.row as AdviseSupplier;
      return (
        <div className="flex gap-2">
          <button
            className="text-sm text-blue-600 hover:underline"
            onClick={() => handleEdit(row)}>
            Edit
          </button>
          <button
            className="text-sm text-red-600 hover:underline"
            onClick={() => setDeleteTarget(row)}>
            Delete
          </button>
        </div>
      );
    }
  };

  const columns = canEdit ? [actionsColumn, ...SupplierGridColumnDefs] : SupplierGridColumnDefs;

  const pageActions = (
    <PermissibleRender requiredPermissions={[Permission.ADVISE_FULL]}>
      <button
        type="button"
        className={DEFAULT_ADVANCE_BUTTON_STYLE}
        onClick={handleCreate}>
        Add Supplier
      </button>
    </PermissibleRender>
  );

  return (
    <PageLayout
      pageTitle="Advise Suppliers"
      pageActions={pageActions}
      dataCy="advise-suppliers-page">
      <AdviseSubNav />
      <PageCard
        title="Suppliers"
        childrenWrapperClass="min-h-80 min-w-full">
        <UpstackDataGrid
          dataCy="advise-suppliers-grid"
          title="Suppliers"
          rows={suppliers}
          columns={columns}
          loadingData={loading}
          page={'advise_supplier_grid'}
          gridSettings={{ muiConfig: {}, disabledFields: [] }}
          showSearch={true}
        />
      </PageCard>

      <SupplierFormDialog
        open={showForm}
        onClose={() => setShowForm(false)}
        onSave={handleSave}
        supplier={editingSupplier}
        directory={directory}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Supplier"
        message={`Are you sure you want to delete "${deleteTarget?.supplier_name}"?`}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </PageLayout>
  );
};
